import { useEffect, useRef } from 'react'
import styled from '@emotion/styled'
import { scrollRowTo } from 'utils/nestedMenu'

const SubcategoryNavRow = styled.div`
  position: relative;
  width: 100%;
  overflow-x: auto;
  scrollbar-width: none;
  user-select: none;

  &[data-overflow='true'] {
    cursor: grab;
  }

  &[data-dragging='true'],
  &[data-dragging='true'] * {
    cursor: grabbing;
  }

  &::-webkit-scrollbar {
    display: none;
  }
`

// Tabs keep their natural width, left aligned, and the row scrolls when they
// overflow. On mobile, two tabs share the width.
const SubcategoryNavList = styled.ul<{ count: number }>`
  display: flex;
  min-width: 100%;
  width: max-content;

  li {
    flex: 0 0 auto;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    width: ${(props) => (props.count === 2 ? '100%' : 'max-content')};

    li {
      flex: ${(props) => (props.count === 2 ? '1 1 50%' : '0 0 auto')};
    }
  }
`

const SubcategoryNavButton = styled.button<{ active?: boolean }>`
  display: block;
  width: 100%;
  padding: 1.2rem 1.6rem 1rem;
  white-space: nowrap;
  font-family: ${(props) => props.theme.fonts.preface.family};
  font-weight: ${(props) => props.theme.fonts.preface.weight};
  font-style: ${(props) => props.theme.fonts.preface.fontStyle};
  letter-spacing: ${(props) => props.theme.fonts.preface.letterSpacing};
  text-transform: ${(props) => props.theme.fonts.preface.textTransform};
  -webkit-font-smoothing: ${(props) => props.theme.fonts.preface.fontSmoothing};
  font-size: ${(props) => props.theme.fonts.preface.fontSize};
  color: ${(props) => props.theme.links.dark[props.active ? 'hover' : 'color']};
  border-bottom: 0.2rem solid
    ${(props) => (props.active ? props.theme.links.dark.hover : 'transparent')};

  &:hover,
  &:focus-visible {
    color: ${(props) => props.theme.links.dark.hover};
  }
`

export interface SubcategoryNavItem {
  id: string
  name: string
}

const DRAG_THRESHOLD = 5

// Touch and trackpad scroll the row natively. This adds mouse dragging, and
// swallows the click that follows a drag so it does not select a tab.
const useDragScroll = (rowRef: React.RefObject<HTMLDivElement>) => {
  const suppressClick = useRef(false)
  const stopDrag = useRef<(() => void) | null>(null)

  useEffect(() => () => stopDrag.current?.(), [])

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    const row = rowRef.current
    if (!row || event.pointerType !== 'mouse' || event.button !== 0) return
    if (row.scrollWidth <= row.clientWidth) return
    const startX = event.clientX
    const startLeft = row.scrollLeft
    let dragged = false

    const onMove = (move: PointerEvent) => {
      const distance = move.clientX - startX
      if (!dragged && Math.abs(distance) < DRAG_THRESHOLD) return
      dragged = true
      row.dataset.dragging = 'true'
      row.scrollLeft = startLeft - distance
    }
    const onEnd = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onEnd)
      window.removeEventListener('pointercancel', onEnd)
      delete row.dataset.dragging
      stopDrag.current = null
      if (dragged) {
        suppressClick.current = true
        setTimeout(() => {
          suppressClick.current = false
        }, 0)
      }
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onEnd)
    window.addEventListener('pointercancel', onEnd)
    stopDrag.current = onEnd
  }

  const onClickCapture = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!suppressClick.current) return
    suppressClick.current = false
    event.preventDefault()
    event.stopPropagation()
  }

  return { onPointerDown, onClickCapture }
}

const SubcategoryNav = ({
  label,
  items,
  activeId,
  onSelect,
}: {
  label: string
  items: SubcategoryNavItem[]
  activeId: string | null
  onSelect: (id: string) => void
}) => {
  const rowRef = useRef<HTMLDivElement | null>(null)
  const drag = useDragScroll(rowRef)

  useEffect(() => {
    const row = rowRef.current
    if (!row) return
    const measure = () => {
      row.dataset.overflow = String(row.scrollWidth > row.clientWidth + 1)
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(row)
    return () => observer.disconnect()
  }, [items.length])

  useEffect(() => {
    const row = rowRef.current
    if (!row || !activeId || row.scrollWidth <= row.clientWidth) return
    const item = Array.from(
      row.querySelectorAll<HTMLElement>('[data-subcategory-id]')
    ).find((i) => i.dataset.subcategoryId === activeId)
    if (item) scrollRowTo(row, Math.max(0, item.offsetLeft - 24))
  }, [activeId])

  if (items.length === 0) return null

  return (
    <nav aria-label={label}>
      <SubcategoryNavRow ref={rowRef} {...drag}>
        <SubcategoryNavList count={items.length}>
          {items.map((item) => {
            const isActive = item.id === activeId
            return (
              <li key={item.id} data-subcategory-id={item.id}>
                <SubcategoryNavButton
                  type="button"
                  active={isActive}
                  aria-current={isActive ? 'true' : undefined}
                  onClick={() => onSelect(item.id)}
                >
                  {item.name}
                </SubcategoryNavButton>
              </li>
            )
          })}
        </SubcategoryNavList>
      </SubcategoryNavRow>
    </nav>
  )
}

export default SubcategoryNav
